import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MissionService } from '../../../../services/mission.service';
import { FlagService } from '../../../../shared/services/flag.service';
import { FlagType } from '../../../../shared/utils/enums';
import { displayImg } from '../../../../shared/utils/utils.functions';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
    selector: 'app-assign-candidate-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatSliderModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        SharedModule,
    ],
    templateUrl: './assign-candidate-dialog.component.html',
    styleUrl: './assign-candidate-dialog.component.scss',
})
export class AssignCandidateDialogComponent implements OnInit, OnDestroy {
    mission: any;
    candidates: any[] = [];
    isLoadingCandidates = false;
    isLoadingAvailability = false;
    isAssigning = false;

    // Filters
    searchName = '';
    maxDistance = 50;
    // Lot D (client clinique) : « Ignorer la distance » cochée par défaut — le slider est désactivé
    // quand cochée ; la liste est rechargée à chaque bascule.
    ignoreDistance = true;
    // Lot D : seuls les pros éligibles sont affichés ; compteur des masqués.
    hiddenIneligibleCount = 0;

    // Selected candidate
    selectedCandidate: any = null;

    // Fragment calendar (for fragmentable missions)
    step: 'select-candidate' | 'select-dates' | 'review' | 'confirm' = 'select-candidate';
    calendarDays: any[] = [];
    selectedDates: Set<string> = new Set();
    nonAvailabilityDates: any[] = [];
    assignedFragmentDates: any[] = [];
    hasCalendarConflict = false;
    isConflictWithSameMission = false;
    missionExpiresAt: any ;

    totalCandidates = 0;

    // Confirmation overlay + rotating progress loader
    showConfirmOverlay = false;
    showProgressLoader = false;
    progressMessage = '';
    private progressMessages: string[] = [
        'Affectation du professionnel en cours...',
        'Génération du contrat...',
        'Envoi des notifications...',
        'Finalisation de l\'affectation...',
    ];
    private progressMessageIndex = 0;
    private progressInterval: ReturnType<typeof setInterval> | null = null;

    private countdownInterval: ReturnType<typeof setInterval> | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<AssignCandidateDialogComponent>,
        private missionService: MissionService,
        private flagService: FlagService
    ) {
        this.mission = data.mission;
    }

    ngOnInit(): void {
        this.loadCandidates();
    }

    loadCandidates(): void {
        this.isLoadingCandidates = true;
        this.missionService
            .getCandidatesByDistance(
                this.mission.id,
                this.maxDistance,
                this.searchName,
                0,
                50,
                this.ignoreDistance,
                true
            )
            .subscribe({
                next: (res: any) => {
                    this.candidates = res.candidates || [];
                    this.totalCandidates = res.totalItems || 0;
                    this.hiddenIneligibleCount = res.hiddenIneligibleCount || 0;
                    this.isLoadingCandidates = false;
                },
                error: () => {
                    this.candidates = [];
                    this.totalCandidates = 0;
                    this.hiddenIneligibleCount = 0;
                    this.isLoadingCandidates = false;
                },
            });
    }

    onSearchChange(): void {
        this.loadCandidates();
    }

    onDistanceChange(): void {
        this.loadCandidates();
    }

    /** Lot D : bascule « Ignorer la distance » → rechargement de la liste. */
    onIgnoreDistanceChange(): void {
        this.loadCandidates();
    }

    selectCandidate(candidate: any): void {
        this.selectedCandidate = candidate;

        if (this.mission.isfragmentable) {
            // Load availability and show calendar
            this.step = 'select-dates';
            this.loadAvailabilityAndBuildCalendar();
        } else {
            this.step = 'confirm';
            this.loadAvailabilityForConfirm();
        }
    }

    private loadAvailabilityForConfirm(): void {
        this.isLoadingAvailability = true;
        this.missionService
            .getCandidateAvailability(this.selectedCandidate.id, this.mission.id)
            .subscribe({
                next: (res: any) => {
                    this.hasCalendarConflict = res.hasCalendarConflict || false;
                    this.isConflictWithSameMission = res.isConflictWithSameMission || false;
                    this.isLoadingAvailability = false;
                    this.missionExpiresAt = res.missionExpiresAt;
                },
                error: () => {
                    this.isLoadingAvailability = false;
                },
            });
    }

    private loadAvailabilityAndBuildCalendar(): void {
        this.isLoadingAvailability = true;
        this.missionService
            .getCandidateAvailability(this.selectedCandidate.id, this.mission.id)
            .subscribe({
                next: (res: any) => {
                    this.nonAvailabilityDates = res.nonAvailabilityDates || [];
                    this.assignedFragmentDates = res.assignedFragmentDates || [];
                    this.hasCalendarConflict = res.hasCalendarConflict || false;
                    this.missionExpiresAt = res.missionExpiresAt;                    
                    this.buildCalendar(res.missionStartDate, res.missionEndDate);
                    this.isLoadingAvailability = false;
                },
                error: () => {
                    this.isLoadingAvailability = false;
                },
            });
    }

    private buildCalendar(startDateStr: string, endDateStr: string): void {
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);
        this.calendarDays = [];
        this.selectedDates = new Set();
        this.clearCountdown();

        const current = new Date(start);
        while (current <= end) {
            const dateStr = this.formatDate(current);

            // Check if date is already assigned and by whom
            const assignedFragment = this.assignedFragmentDates.find(
                (f: any) => f.startDate === dateStr
            );
            const isAssigned = !!assignedFragment;
            const isAssignedToSelected = isAssigned && assignedFragment?.candidateId === this.selectedCandidate?.id;
            const isAssignedToOther = isAssigned && !isAssignedToSelected;

            // Check if candidate is NOT available on this date
            // If the day is assigned to the selected candidate, don't mark it as non-available
            const isNonAvailable = isAssignedToSelected ? false : this.nonAvailabilityDates.some((nad: any) => {
                const nadStart = new Date(nad.startDate);
                const nadEnd = new Date(nad.endDate);
                const checkDate = new Date(dateStr);
                return checkDate >= nadStart && checkDate <= nadEnd;
            });

            this.calendarDays.push({
                date: new Date(current),
                dateStr: dateStr,
                label: current.toLocaleDateString('fr-FR', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                }),
                isAssigned: isAssigned,
                isAssignedToSelected: isAssignedToSelected,
                isAssignedToOther: isAssignedToOther,
                isNonAvailable: isNonAvailable,
                isSelectable: !isAssigned && !isNonAvailable,
                // Status of the assigned fragment (ACCEPTED or SIGNED)
                status: isAssignedToSelected ? (assignedFragment?.status ?? null) : null,
                // expiresAt present only for ACCEPTED fragments (not SIGNED)
                expiresAt: isAssignedToSelected && assignedFragment?.status === 'ACCEPTED' ? (assignedFragment?.expiresAt ?? null) : null,
                countdownText: null as string | null,
                countdownUrgency: null as string | null,
            });

            current.setDate(current.getDate() + 1);
        }

        // Kick off the live countdown for assigned-self days that have an expiry
        const hasCountdownDays = this.calendarDays.some((d) => d.expiresAt);
        if (hasCountdownDays) {
            this.startCountdownTimer();
        }
    }

    toggleDate(day: any): void {
        if (!day.isSelectable) return;
        if (this.selectedDates.has(day.dateStr)) {
            this.selectedDates.delete(day.dateStr);
        } else {
            this.selectedDates.add(day.dateStr);
        }
    }

    isDateSelected(day: any): boolean {
        return this.selectedDates.has(day.dateStr);
    }

    selectAllAvailable(): void {
        this.calendarDays
            .filter((d) => d.isSelectable)
            .forEach((d) => this.selectedDates.add(d.dateStr));
    }

    deselectAll(): void {
        this.selectedDates.clear();
    }

    get canConfirm(): boolean {
        if (this.isAssigning) return false;
        if (!this.selectedCandidate) return false;
        if (this.mission.isfragmentable) {
            // In review step, dates are already locked in — just check there are some
            return this.selectedDates.size > 0;
        }
        // DONT Allow assignment if the conflict is because this mission is already assigned to this candidate
        // if (this.hasCalendarConflict && this.isConflictWithSameMission) return true;
        return !this.hasCalendarConflict;
    }

    /**
     * Triggered by the footer button. Opens a "Are you sure?" confirmation overlay
     * instead of firing the assignment immediately, so the user has a final chance
     * to review before the operation starts.
     */
    confirmAssignment(): void {
        if (!this.canConfirm) return;
        this.showConfirmOverlay = true;
    }

    /** Cancel the confirmation overlay without assigning. */
    cancelConfirm(): void {
        if (this.isAssigning) return;
        this.showConfirmOverlay = false;
    }

    /**
     * User clicked "Oui" on the confirmation overlay — actually fire the direct
     * assignment, rotating progress messages while the backend works.
     */
    proceedWithAssignment(): void {
        if (!this.canConfirm) return;
        this.isAssigning = true;
        this.startProgressLoader();

        const body: any = {
            candidatId: this.selectedCandidate.id,
            missionId: this.mission.id,
        };

        if (this.mission.isfragmentable) {
            body.fragmentDates = Array.from(this.selectedDates).map((dateStr) => ({
                startDate: dateStr,
                endDate: dateStr,
            }));
        }

        this.missionService.directAssign(body).subscribe({
            next: () => {
                // Note: do NOT send a chat message here. At this point the contract is
                // only pending validation by the agency — the candidate should not yet
                // receive a "please sign your contract" notification. The chat message
                // (with the sign URL button) is pushed from the backend when the agency
                // actually validates and sends the mission contract.
                this.stopProgressLoader();
                this.isAssigning = false;
                this.showConfirmOverlay = false;

                this.flagService.displayFlag(
                    'Mission affectée avec succès !',
                    FlagType.success
                );

                this.dialogRef.close('assigned');
            },
            error: (err) => {
                console.error('Error during assignment:', err);
                this.stopProgressLoader();
                this.isAssigning = false;
                // Keep the overlay open so the user can retry or cancel explicitly
                this.flagService.displayFlag(
                    'Erreur lors de l\'affectation',
                    FlagType.error
                );
            },
        });
    }

    // ── Progress loader helpers ──────────────────────────────────────────────

    private startProgressLoader(): void {
        this.progressMessageIndex = 0;
        this.progressMessage = this.progressMessages[0];
        this.showProgressLoader = true;
        this.progressInterval = setInterval(() => {
            this.progressMessageIndex =
                (this.progressMessageIndex + 1) % this.progressMessages.length;
            this.progressMessage = this.progressMessages[this.progressMessageIndex];
        }, 2000);
    }

    private stopProgressLoader(): void {
        if (this.progressInterval !== null) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
        this.showProgressLoader = false;
        this.progressMessage = '';
    }

    ngOnDestroy(): void {
        this.clearCountdown();
        this.stopProgressLoader();
    }

    goBack(): void {
        if (this.step === 'review') {
            this.step = 'select-dates';
        } else if (this.step === 'confirm' || this.step === 'select-dates') {
            this.clearCountdown();
            this.step = 'select-candidate';
            this.selectedCandidate = null;
            this.selectedDates = new Set();
        }
    }

    goToReview(): void {
        if (this.selectedDates.size > 0) {
            this.step = 'review';
        }
    }

    get selectedDatesArray(): string[] {
        return Array.from(this.selectedDates)
            .sort()
            .map(d => {
                const date = new Date(d + 'T00:00:00');
                return date.toLocaleDateString('fr-FR', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                });
            });
    }

    close(): void {
        // Do not let the user dismiss mid-assignment — the backend is running
        // and closing the dialog would leave the UI unable to react to the result.
        if (this.isAssigning) return;
        this.clearCountdown();
        this.stopProgressLoader();
        this.dialogRef.close();
    }

    // ── Countdown helpers ────────────────────────────────────────────────────

    private startCountdownTimer(): void {
        this.updateCountdowns();
        this.countdownInterval = setInterval(() => this.updateCountdowns(), 60_000);
    }

    private clearCountdown(): void {
        if (this.countdownInterval !== null) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }

    private updateCountdowns(): void {
        const now = Date.now();
        this.calendarDays.forEach((day) => {
            // Only update countdown for ACCEPTED fragments with expiresAt
            if (!day.expiresAt || day.status !== 'ACCEPTED') {
                day.countdownText = null;
                day.countdownUrgency = null;
                return;
            }
            const msLeft = new Date(day.expiresAt).getTime() - now;
            if (msLeft <= 0) {
                day.countdownText = 'Expiré';
                day.countdownUrgency = 'expired';
            } else {
                const totalMinutes = Math.floor(msLeft / 60_000);
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                day.countdownText = `${hours}h ${String(minutes).padStart(2, '0')}m`;
                day.countdownUrgency = hours < 12 ? 'urgent' : hours < 24 ? 'warning' : 'ok';
            }
        });
    }

    public getRemainingTimeString(dateString: string): string {
        if (!dateString) {
            return "";
        }
        const now = new Date().getTime();
        const target = new Date(dateString).getTime();
        const diff = target - now;

        if (diff <= 0) return "Expiré";

        const seconds = Math.floor((diff / 1000) % 60);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        return `${days}j ${hours}h ${minutes}min`;
    }

    private formatDate(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    protected readonly displayImg = displayImg;
}
